import { useEffect, useRef, useState } from 'react';
import { Input } from '../ui/Input.js';
import { Button } from '../ui/Button.js';
import { Sheet } from '../ui/Sheet.js';
import { Field } from '../ui/Field.js';
import { ConfirmDialog } from '../ui/ConfirmDialog.js';
import { useCalories } from '../stores/calories.js';
import type { Food } from '../api/food.js';
import { errorMessage } from '../lib/errors.js';

interface Props {
  open: boolean;
  onClose: () => void;
  food: Food | null;
}

function num(value: string): number | null {
  const n = Number(value.replace(',', '.'));
  return value.trim() === '' || Number.isNaN(n) ? null : n;
}

function str(value: number | null): string {
  return value === null ? '' : String(value);
}

/** Where a value Claude guessed wrong gets corrected, once, for every future log. */
export function FoodEditModal({ open, onClose, food }: Props): React.ReactElement | null {
  const addFood = useCalories((s) => s.addFood);
  const editFood = useCalories((s) => s.editFood);
  const removeFood = useCalories((s) => s.removeFood);

  const [name, setName] = useState('');
  const [kcal100, setKcal100] = useState('');
  const [kcalPortion, setKcalPortion] = useState('');
  const [prot100, setProt100] = useState('');
  const [protPortion, setProtPortion] = useState('');
  const [portionLabel, setPortionLabel] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setConfirming(false);
    setName(food?.name ?? '');
    setKcal100(str(food?.kcalPer100g ?? null));
    setKcalPortion(str(food?.kcalPerPortion ?? null));
    setProt100(str(food?.proteinPer100g ?? null));
    setProtPortion(str(food?.proteinPerPortion ?? null));
    setPortionLabel(food?.portionLabel ?? '');
  }, [open, food]);

  if (!open) return null;

  const values = {
    kcalPer100g: num(kcal100),
    kcalPerPortion: num(kcalPortion),
    proteinPer100g: num(prot100),
    proteinPerPortion: num(protPortion),
    portionLabel: portionLabel.trim() === '' ? null : portionLabel.trim(),
  };
  const hasKcal = values.kcalPer100g !== null || values.kcalPerPortion !== null;

  async function submit(): Promise<void> {
    if (submitting || !name.trim() || !hasKcal) return;
    setSubmitting(true);
    setError(null);
    try {
      if (food) await editFood(food.foodId, { name: name.trim(), ...values });
      else await addFood({ name: name.trim(), ...values });
      onClose();
    } catch (err) {
      setError(errorMessage(err, 'Erreur'));
    } finally {
      setSubmitting(false);
    }
  }

  async function destroy(): Promise<void> {
    if (!food) return;
    await removeFood(food.foodId);
    onClose();
  }

  const title = food ? 'Modifier l’aliment' : 'Nouvel aliment';

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={title}
      closeLabel="Fermer"
      initialFocusRef={nameRef}
      footer={
        <>
          {food && (
            <Button variant="danger" className="mr-auto" onClick={() => setConfirming(true)}>
              Supprimer
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={!name.trim() || !hasKcal}
            loading={submitting}
          >
            Enregistrer
          </Button>
        </>
      }
    >
      <div className="pile pile-serree">
        <Field label="Nom">
          <Input
            ref={nameRef}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Comté"
          />
        </Field>

        <div className="flex gap-2">
          <Field label="kcal / 100 g" className="flex-1 min-w-0">
            <Input value={kcal100} onChange={(e) => setKcal100(e.target.value)} inputMode="decimal" />
          </Field>
          <Field label="Protéines / 100 g" className="flex-1 min-w-0">
            <Input value={prot100} onChange={(e) => setProt100(e.target.value)} inputMode="decimal" />
          </Field>
        </div>

        <div className="flex gap-2">
          <Field label="kcal / portion" className="flex-1 min-w-0">
            <Input
              value={kcalPortion}
              onChange={(e) => setKcalPortion(e.target.value)}
              inputMode="decimal"
            />
          </Field>
          <Field label="Protéines / portion" className="flex-1 min-w-0">
            <Input
              value={protPortion}
              onChange={(e) => setProtPortion(e.target.value)}
              inputMode="decimal"
            />
          </Field>
        </div>

        <Field label="Nom de la portion">
          <Input
            value={portionLabel}
            onChange={(e) => setPortionLabel(e.target.value)}
            placeholder="une part, un bol…"
          />
        </Field>

        {!hasKcal && (
          <p className="champ-aide">
            Renseigne au moins une valeur en kcal — pour 100 g ou par portion.
          </p>
        )}

        <p className="alerte alerte-erreur" role="alert">
          {error}
        </p>
      </div>

      {food && (
        <ConfirmDialog
          open={confirming}
          title={`Supprimer « ${food.name} » ?`}
          message="Les entrées déjà loguées sont conservées telles quelles."
          confirmLabel="Supprimer"
          cancelLabel="Annuler"
          onConfirm={() => void destroy()}
          onCancel={() => setConfirming(false)}
        />
      )}
    </Sheet>
  );
}
